/**
 * Establishing a Sisense session from the SSO landing page.
 *
 * Sisense's `/jwt` endpoint accepts a one-time token in the query string and
 * answers with a redirect plus the session cookies. The token is single-use:
 * the first request to that URL consumes it, and every later request to the same
 * URL is answered with an HTTP 500 SSO error page. That first request does not
 * have to come from the user — an anonymous, cookie-less GET is enough, so any
 * link scanner, prefetcher or intercepting proxy sitting between the user and
 * Sisense can consume the token and leave the user on a dead-end error page.
 *
 * Navigating the browser straight at the token URL gives us no way to notice
 * that happened. Redeeming it with `fetch` does: the cookies are still set on
 * the Sisense origin, and afterwards we can ask Sisense whether the session took
 * and mint a fresh token if it did not. Once a session exists the browser is
 * sent to a plain Sisense URL carrying no credential, which is what makes the
 * final navigation immune to the same interception.
 *
 * How this behaves per cookie policy, since the redemption is a third-party
 * request and browsers disagree about those:
 *
 * - Third-party writes allowed: the session is established and verified here,
 *   and the credential-free navigation lands authenticated.
 * - Third-party writes blocked (Safari ITP, most private modes): the check below
 *   reports no session, so the caller falls back to navigating at the token URL,
 *   exactly as before this existed.
 * - Third-party writes partitioned (Firefox Total Cookie Protection): the write
 *   and the check share the Forecast-owned partition, so the check reports a
 *   session the top-level navigation cannot see, and Sisense bounces the user
 *   back here. The second pass finds a session already present in that partition
 *   and defers to the token URL, so it resolves — at the cost of one extra
 *   bounce. Those users are no worse off than before, but they do not gain the
 *   protection either. Detecting partitioning from script is not possible
 *   without the Storage Access API, so this is accepted rather than solved.
 */

const PRODUCTION_ORIGIN = 'https://forecast.sisense.com';
const DEVELOPMENT_ORIGIN = 'https://forecast-dev.sisense.com';

/** How many fresh tokens to burn through before giving up and letting the caller fall back. */
export const MAX_SESSION_ATTEMPTS = 3;

/**
 * Ceiling on each Sisense request. The users this exists for sit behind
 * intercepting proxies, which stall requests rather than failing them, and this
 * runs on a route that shows only a loader — without a bound, a stalled request
 * holds the user there instead of falling back.
 */
export const REQUEST_TIMEOUT_MS = 5000;

/**
 * Ceiling on the whole handshake. Per-request timeouts alone still multiply out
 * across attempts, so this caps the total wait before the caller falls back,
 * however many individual requests stall.
 */
export const HANDSHAKE_TIMEOUT_MS = 15000;

/** Why the handshake ended, so the caller can tell "retry differently" from "already failed". */
export type SisenseSessionOutcome =
	/** A session exists and was verified; navigate somewhere credential-free. */
	| 'established'
	/** No session, but a token can still be handed to Sisense the original way. */
	| 'fall-back'
	/** No token could be minted. The failure has already been surfaced to the user. */
	| 'mint-failed';

export const getSisenseOrigin = (): string =>
	process.env.CIRCLE_BRANCH === 'production' ? PRODUCTION_ORIGIN : DEVELOPMENT_ORIGIN;

/**
 * Reduce a raw `return_to` to a destination that is safe to hand to either
 * navigation path, or null when it points anywhere other than Sisense.
 *
 * Both paths need the same answer: one of them forwards `return_to` to Sisense
 * inside a URL that gets fetched with credentials, so an unchecked value there
 * would mean silent authenticated requests to whatever host it named.
 */
export const normalizeReturnTo = (returnTo?: string | null): string | null => {
	if (!returnTo) {
		return null;
	}
	if (returnTo.startsWith('/')) {
		// `//host` and `/\host` are authority references, not paths - they would send
		// the browser (or Sisense) off-origin.
		return /^\/[/\\]/.test(returnTo) ? null : returnTo;
	}
	try {
		return new URL(returnTo).origin === getSisenseOrigin() ? returnTo : null;
	} catch {
		return null;
	}
};

/**
 * The fragment to append. A destination that carries its own wins: appending
 * would produce a second '#' and leave Sisense's router with an unmatchable route.
 */
const effectiveHash = (returnTo: string | null, hash?: string): string =>
	returnTo && returnTo.includes('#') ? '' : hash || '';

/**
 * The URL that redeems a token. `returnTo` and `hash` are carried through so the
 * fallback navigation lands the user on the page they originally asked for.
 */
export const buildJwtRedemptionUrl = (jwt: string, returnTo?: string | null, hash?: string): string => {
	const destination = normalizeReturnTo(returnTo);
	let url = `${getSisenseOrigin()}/jwt?jwt=${jwt}`;
	if (destination) {
		url += `&return_to=${encodeURIComponent(destination)}`;
	}
	// Appended whether or not a destination survived, so both navigation paths
	// land on the same page for the same input.
	return `${url}${effectiveHash(destination, hash)}`;
};

/**
 * Where to send the browser once a session exists. Anything that is not a
 * Sisense destination falls back to the Sisense home page, so a crafted
 * `return_to` cannot turn this into an open redirect.
 */
export const resolveSisenseDestination = (returnTo?: string | null, hash?: string): string => {
	const origin = getSisenseOrigin();
	const destination = normalizeReturnTo(returnTo);
	const suffix = effectiveHash(destination, hash);
	if (!destination) {
		return `${origin}/app/main${suffix}`;
	}
	return destination.startsWith('/') ? `${origin}${destination}${suffix}` : `${destination}${suffix}`;
};

const fetchWithTimeout = async (url: string, init: RequestInit, timeoutMs: number): Promise<Response> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	try {
		return await fetch(url, {...init, signal: controller.signal});
	} finally {
		clearTimeout(timer);
	}
};

/**
 * Whether the browser currently holds a working Sisense session.
 *
 * Sisense allows credentialed cross-origin reads from the Forecast app, so the
 * status of this call is readable — which is the whole reason the fetch-based
 * redemption can be verified at all.
 */
export const isSisenseSessionActive = async (timeoutMs: number = REQUEST_TIMEOUT_MS): Promise<boolean> => {
	try {
		const response = await fetchWithTimeout(
			`${getSisenseOrigin()}/api/v1/dashboards?fields=title`,
			{credentials: 'include'},
			timeoutMs
		);
		return response.status === 200;
	} catch {
		return false;
	}
};

/**
 * Redeem tokens until Sisense reports a live session.
 *
 * `mintJwt` must return a *fresh* token on every call — retrying with an already
 * consumed one can never succeed.
 */
export const establishSisenseSession = async (
	mintJwt: () => Promise<string | null | undefined>,
	returnTo?: string | null,
	hash?: string,
	maxAttempts: number = MAX_SESSION_ATTEMPTS,
	handshakeTimeoutMs: number = HANDSHAKE_TIMEOUT_MS
): Promise<SisenseSessionOutcome> => {
	const startedAt = Date.now();
	const budget = () => Math.min(REQUEST_TIMEOUT_MS, handshakeTimeoutMs - (Date.now() - startedAt));

	// A session that predates this handshake makes the check below meaningless: it
	// would report success without proving the redemption worked, and the browser
	// could be carrying someone else's session or another tenant's. Only navigating
	// at the token URL re-asserts who the user is, so defer to that.
	if (await isSisenseSessionActive(budget())) {
		return 'fall-back';
	}

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		if (budget() <= 0) {
			return 'fall-back';
		}
		// eslint-disable-next-line no-await-in-loop
		const jwt = await mintJwt();
		if (!jwt) {
			return 'mint-failed';
		}
		try {
			// no-cors: we cannot read this response, and do not need to - the point is
			// the Set-Cookie it carries. Success is confirmed separately below.
			// eslint-disable-next-line no-await-in-loop
			await fetchWithTimeout(
				buildJwtRedemptionUrl(jwt, returnTo, hash),
				{credentials: 'include', mode: 'no-cors'},
				budget()
			);
		} catch {
			// A network-level failure here is indistinguishable from a consumed token,
			// so fall through to the check and let the next attempt decide.
		}
		// eslint-disable-next-line no-await-in-loop
		if (await isSisenseSessionActive(budget())) {
			return 'established';
		}
	}
	return 'fall-back';
};

/**
 * The URL to send the browser to in order to reach Sisense as the current user,
 * or null when no token could be obtained at all (in which case `mintJwt` has
 * already surfaced the failure to the user).
 */
export const resolveSisenseNavigation = async ({
	mintJwt,
	returnTo,
	hash,
}: {
	mintJwt: () => Promise<string | null | undefined>;
	returnTo?: string | null;
	hash?: string;
}): Promise<string | null> => {
	const outcome = await establishSisenseSession(mintJwt, returnTo, hash);
	if (outcome === 'established') {
		// The session is already on the Sisense origin, so the browser can go to a
		// plain URL that carries no credential.
		return resolveSisenseDestination(returnTo, hash);
	}
	if (outcome === 'mint-failed') {
		// Minting already failed once and said so; asking again would only stack a
		// second error on the same click.
		return null;
	}
	const jwt = await mintJwt();
	return jwt ? buildJwtRedemptionUrl(jwt, returnTo, hash) : null;
};
